import { FC, useEffect, useState } from 'react';

import { LoaderFunctionArgs } from '@remix-run/node';
import { Link, useSearchParams } from '@remix-run/react';
import CustomPagination from '~/components/custom-pagination';
import { Home } from '~/components/images/Home';
import Layout from '~/components/layout';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '~/components/ui/breadcrumb';
import { Button } from '~/components/ui/button';
import { capitalizeFirstLetter, formatNumber } from '~/lib/utils';
import { validateUser } from '~/services/api/auth';
import { getCategoryList } from '~/services/api/categories';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';

interface ExploreCategoriesProps {
  data: Awaited<ReturnType<typeof getCategoryList>>;
  title?: boolean;
  onCategoryClick?: (categoryName: string) => void;
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const limit = Number(url.searchParams.get('limit')) || 12;
  const page = Number(url.searchParams.get('page')) || undefined;
  const selectedCategory = url.searchParams.get('category');

  const { user } = await validateUser(request);
  const categories = await getCategoryList(limit, page);

  return typedjson({
    categories,
    user,
    selectedCategory,
  });
};

const CategoryItem: FC<{
  category: Awaited<ReturnType<typeof getCategoryList>>['list'][0];
  onClick: (categoryName: string) => void;
}> = ({ category, onClick }) => {
  // const IconComponent = iconMapping[category.image];

  return (
    <Link onClick={() => onClick(category.name)} to={`/products?category=${category.name}`}>
      <div className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg bg-grey1 p-4">
        {/* <div className="my-1 rounded-full bg-white p-3">{IconComponent}</div> */}
        <img src={category.image} alt={category.name} className="h-20 w-20" />
        <p className="mt-3 text-sm font-medium text-black">{category.name}</p>
        <p className="text-xs font-normal text-slate-500">{formatNumber(category.productsToCategories.length)} items</p>
        <Button variant={'ghostOutline'} className="w-full">
          Explore
        </Button>
      </div>
    </Link>
  );
};

const Categories: FC<ExploreCategoriesProps> = ({ onCategoryClick }) => {
  const data = useTypedLoaderData<typeof loader>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedCategory, setSelectedCategory] = useState(data.selectedCategory || '');

  const currentPage = data.categories.page;
  const totalPages = Math.ceil(data.categories.totalCount / data.categories.limit);

  useEffect(() => {
    const searchParamsCategory = searchParams.get('category');

    if (searchParamsCategory) {
      setSelectedCategory(searchParamsCategory);
    }
  }, [searchParams]);

  return (
    <Layout user={data.user as unknown as NonNullable<Awaited<ReturnType<typeof validateUser>>['user']>}>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/" className="flex flex-row items-center gap-1 text-sm font-normal">
              <Home className="h-5 w-5" />
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          {selectedCategory ? (
            <>
              <BreadcrumbItem>
                <BreadcrumbLink href="/products" className="text-sm font-normal">
                  Products
                </BreadcrumbLink>
              </BreadcrumbItem>
              {selectedCategory && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="text-sm font-normal">
                      {capitalizeFirstLetter(selectedCategory)}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )}
            </>
          ) : (
            <>
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <BreadcrumbLink href="/categories" className="text-sm font-normal">
                    Categories
                  </BreadcrumbLink>
                </BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>
      <div className="mb-2 mt-4 flex flex-col">
        <h1 className="text-2xl font-semibold">Categories</h1>
        <span className="text-sm text-slate-600">{data.categories.totalCount} categories found</span>
      </div>
      <hr className="mb-4 h-[1px] bg-slate-200" />

      <div className="col-span-3 flex flex-col gap-10">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {data.categories.list.length > 0 ? (
            data.categories.list.map((category, index) => (
              <CategoryItem key={index} category={category} onClick={onCategoryClick ?? (() => {})} />
            ))
          ) : (
            <span className="col-col-span-1 text-center text-sm text-slate-500 md:col-span-2 lg:col-span-3">
              No data found
            </span>
          )}
        </div>

        <CustomPagination
          limit={data.categories.limit}
          totalCount={data.categories.totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevPage={() => {
            searchParams.set('page', (currentPage - 1).toString());
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
          onNextPage={() => {
            searchParams.set('page', (currentPage + 1).toString());
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
          onPageChange={(page) => {
            searchParams.set('page', page);
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
          onLimitChange={(limit) => {
            searchParams.set('limit', limit);
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
        />
      </div>
    </Layout>
  );
};

export default Categories;
